import React, { useState } from "react";
import { Skeleton, Table } from "antd";
import { CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { MaterialCategorySummary, MaterialCategoryTotal } from "../../../../api/SiteDaily";
import { fmtQty } from "../executionNorm";
import { fmtPerUnit, perUnitLabel, perUnitOf } from "./daySheet";
import { UNCATEGORIZED } from "./materialCategories";

// The day's material consumption by category, above the task tree: target (the
// norm for what was produced) against actual (what was recorded), for EVERY task
// in scope — not just the ones the clerk happened to open. Lines load lazily per
// task, so this can only come from the server, and it does: one rollup call
// whose task set is the sheet's own.

const MUTED = "#8094ae";
const INK = "#022c57";

const amount = (value: string) => (value === "" ? "—" : `₹ ${fmtQty(value)}`);

const Delta: React.FC<{ target: string; actual: string }> = ({ target, actual }) => {
  if (target === "" || actual === "" || Number(target) <= 0) return null;
  const pct = ((Number(actual) - Number(target)) / Number(target)) * 100;
  return (
    <span className={`pms-exec-delta${pct > 0.5 ? " pms-exec-hint--red" : ""}`}>
      {pct > 0 ? "+" : ""}
      {pct.toFixed(1)}%
    </span>
  );
};

const MaterialCategoryRollup: React.FC<{
  summary: MaterialCategorySummary | null;
  loading: boolean;
}> = ({ summary, loading }) => {
  // Closed by default: the header line IS the summary, and the sheet below is
  // what the clerk came to work on. Opening it is a deliberate look.
  const [open, setOpen] = useState(false);

  if (loading && !summary) return <Skeleton active title={false} paragraph={{ rows: 2 }} />;
  if (!summary || !summary.categories.length) return null;

  // A per-unit column only exists when one work unit covers the whole day.
  const unit = summary.perUnitWorkUomName ? perUnitLabel(summary.perUnitWorkUomName) : "";
  const perUnitCell = (value: string) => (
    <span className="exec-num" style={{ color: value === "" ? MUTED : "#526484" }}>
      {value === "" ? "—" : fmtPerUnit(Number(value))}
    </span>
  );

  // The day's own ₹ per unit, under each header total — the figure that makes
  // the summary readable when the accordion is closed. Divides by the day's
  // ACTUAL output, and exists only where one work unit covers the scope.
  const dayFigure = (label: string, value: string) => {
    const per = perUnitOf(value === "" ? null : Number(value), summary.producedQty);
    return (
      <span className="pms-exec-heads-figure">
        <span className="pms-exec-heads-subtotal">
          {label} {amount(value)}
        </span>
        {per !== null && (
          <span className="pms-exec-per-unit">
            ₹ {fmtPerUnit(per)} / {unit}
          </span>
        )}
      </span>
    );
  };

  const columns: any[] = [
    {
      title: "Category",
      key: "category",
      render: (_: any, row: MaterialCategoryTotal) => (
        <span>
          <span style={{ color: INK, fontWeight: 600 }}>{row.categoryName || UNCATEGORIZED}</span>
          <div className="pms-exec-hint">
            {row.lineCount} material{row.lineCount === 1 ? "" : "s"} · {row.taskCount} task
            {row.taskCount === 1 ? "" : "s"}
          </div>
        </span>
      ),
    },
    {
      title: "Target",
      key: "target",
      width: 150,
      align: "right" as const,
      render: (_: any, row: MaterialCategoryTotal) => (
        <span className="exec-num" style={{ color: row.targetAmount === "" ? MUTED : "#526484" }}>
          {amount(row.targetAmount)}
        </span>
      ),
    },
    {
      title: "Actual",
      key: "actual",
      width: 150,
      align: "right" as const,
      render: (_: any, row: MaterialCategoryTotal) => (
        <span
          className="exec-num"
          style={{ color: row.actualAmount === "" ? MUTED : "#364a63", fontWeight: row.actualAmount === "" ? 400 : 600 }}
        >
          {amount(row.actualAmount)}
        </span>
      ),
    },
    {
      title: "Variance",
      key: "delta",
      width: 110,
      align: "right" as const,
      render: (_: any, row: MaterialCategoryTotal) => <Delta target={row.targetAmount} actual={row.actualAmount} />,
    },
  ];
  if (unit) {
    columns.splice(3, 0, {
      title: `Target ₹ / ${unit}`,
      key: "tPerUnit",
      width: 130,
      align: "right" as const,
      render: (_: any, row: MaterialCategoryTotal) => perUnitCell(row.targetPerUnit),
    });
    columns.splice(4, 0, {
      title: `Actual ₹ / ${unit}`,
      key: "aPerUnit",
      width: 130,
      align: "right" as const,
      render: (_: any, row: MaterialCategoryTotal) => perUnitCell(row.actualPerUnit),
    });
  }

  return (
    <div className="pms-exec-heads-block" style={{ marginBottom: 14 }}>
      <div className="pms-exec-lines-head" style={{ marginBottom: open ? 8 : 0 }}>
        <span
          role="button"
          tabIndex={0}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)}
          style={{ cursor: "pointer" }}
        >
          <span className="pms-eyebrow">
            {open ? <CaretDownOutlined /> : <CaretRightOutlined />} Materials today by category
          </span>
          <div className="pms-exec-hint">
            {summary.categories.length} categor{summary.categories.length === 1 ? "y" : "ies"} across{" "}
            {summary.taskCount} task{summary.taskCount === 1 ? "" : "s"} that produced on this date
            {unit ? (
              <>
                {" "}
                · produced <span className="exec-num">{fmtQty(summary.producedQty)}</span> {unit}
              </>
            ) : (
              // Amounts add across tasks; ₹ per unit does not when the day spans
              // Cum, m, MT and kg at once. Say so rather than printing a number
              // whose denominator means nothing.
              " · mixed work units, so ₹ per unit is shown per task"
            )}
          </div>
        </span>
        <span className="pms-exec-cat-figures">
          {dayFigure("target", summary.targetAmount)}
          {dayFigure("actual", summary.actualAmount)}
          <Delta target={summary.targetAmount} actual={summary.actualAmount} />
        </span>
      </div>
      {open && (
        <Table
          size="small"
          className="pms-exec-heads-table"
          rowKey={(row: MaterialCategoryTotal) => `CAT-${row.categoryId}`}
          dataSource={summary.categories}
          columns={columns}
          pagination={false}
        />
      )}
    </div>
  );
};

export default MaterialCategoryRollup;
